/**
 * Affiliate program — web client helpers for the "Refer & Earn" hub, the
 * application form, the approved-affiliate dashboard, the marketing kit,
 * the welcome banner and Settings → "How you signed up". Thin wrappers
 * over /api/affiliate/* (backend routes/affiliate.js +
 * services/affiliate.service.js).
 *
 * Two paths, one hub:
 *   • Peer referral (lib/referral.js) — bonus credits, automatic.
 *   • Affiliate program (this file) — 10% cash for 6 months via First
 *     Promoter, application required.
 */
import { apiGet, apiPost } from './api';
import { copyText } from './referral';

export const HUB_ROUTE = '/refer-and-earn';
export const APPLY_ROUTE = '/affiliate/apply';
export const DASHBOARD_ROUTE = '/affiliate/dashboard';
export const TERMS_ROUTE = '/affiliate/terms';

/** Fired (window) whenever the affiliate state may have changed. */
export const AFFILIATE_CHANGED_EVENT = 'autonoma:affiliate-changed';

let cache = { at: 0, data: null };

async function json(res) {
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.success === false) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data?.code || null;
    err.fields = data?.errors || data?.fields || null;
    err.data = data;
    throw err;
  }
  return data;
}

/** GET /api/affiliate/status → { program, peer, affiliate, discovery, welcome, kitCount } (cached 30s). */
export async function fetchHubStatus({ force = false, stats = true } = {}) {
  if (!force && cache.data && Date.now() - cache.at < 30_000) return cache.data;
  const data = await json(await apiGet(`/api/affiliate/status${stats ? '' : '?stats=0'}`));
  cache = { at: Date.now(), data };
  return data;
}

export function invalidateHubStatus() {
  cache = { at: 0, data: null };
  try { window.dispatchEvent(new CustomEvent(AFFILIATE_CHANGED_EVENT)); } catch { /* SSR */ }
}

/** POST /api/affiliate/apply → { application, status } (201 new / 200 replay). */
export async function submitApplication(body) {
  const data = await json(await apiPost('/api/affiliate/apply', body));
  invalidateHubStatus();
  return data;
}

/** GET /api/affiliate/stats → live First Promoter numbers (approved only). */
export async function fetchStats({ force = false } = {}) {
  return json(await apiGet(`/api/affiliate/stats${force ? '?force=1' : ''}`));
}

/** GET /api/affiliate/kit → { ideas: [{ …, script_text }], link, linkKind, cta_default }. */
export async function fetchMarketingKit() {
  return json(await apiGet('/api/affiliate/kit'));
}

/** POST /api/affiliate/share { method, ideaId } → { link, text, subject, mailto, x, linkedin, whatsapp, portalUrl }. */
export async function shareAffiliate(method, { ideaId = null } = {}) {
  return json(await apiPost('/api/affiliate/share', { method, ideaId }));
}

/**
 * Run one affiliate share channel end-to-end (records the share, then
 * copies / opens the target). Resolves { copied, payload }.
 */
export async function runAffiliateShare(method, opts = {}) {
  const payload = await shareAffiliate(method === 'twitter' ? 'x' : method, opts);
  if (method === 'copy') return { copied: await copyText(payload.link), payload };
  const url = method === 'email' ? payload.mailto : payload[method === 'twitter' ? 'x' : method];
  if (url) {
    if (method === 'email') window.open(url, '_top');
    else window.open(url, '_blank', 'noopener');
  }
  return { copied: false, payload };
}

/**
 * GET /api/affiliate/qr.svg → data: URL for an <img> / download link.
 * The API lives on another host and needs the App Bridge session token, so
 * a bare <img src> can't load it — fetch authenticated, inline the SVG.
 */
export async function fetchQrDataUrl() {
  const res = await apiGet('/api/affiliate/qr.svg');
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Could not load the QR code (HTTP ${res.status}).`);
  }
  const svg = await res.text();
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** GET /api/affiliate/welcome → { show, affiliateName, summary, daysLeft, expiresAt }. */
export async function fetchAffiliateWelcome() {
  try { return await json(await apiGet('/api/affiliate/welcome')); } catch { return { show: false }; }
}

export async function dismissAffiliateWelcome() {
  try { await apiPost('/api/affiliate/welcome/dismiss', {}); } catch { /* best-effort */ }
}

/** GET /api/affiliate/attribution → { affiliate: { name, code, at, welcomeGrantLabel } | null, peer: {…} | null }. */
export async function fetchAttribution() {
  try { return await json(await apiGet('/api/affiliate/attribution')); } catch { return null; }
}

/** Public: GET /api/affiliate/terms → { title, sections, supportEmail, … }. */
export async function fetchTerms() {
  return json(await apiGet('/api/affiliate/terms'));
}

export const APPLICATION_STATUS_COPY = {
  none: { badge: null, title: 'Apply to become an Autonoma affiliate' },
  pending: { badge: 'Under review', tone: 'attention', title: 'Application under review' },
  approved: { badge: 'Approved', tone: 'success', title: 'You\u2019re an Autonoma affiliate' },
  rejected: { badge: 'Not approved', tone: 'critical', title: 'Application not approved this time' },
};

export function fmtMoney(cents, { currency = 'USD' } = {}) {
  if (cents == null || Number.isNaN(Number(cents))) return '\u2014';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(cents) / 100);
  } catch { return `$${(Number(cents) / 100).toFixed(2)}`; }
}

export function fmtCount(n) {
  if (n == null || Number.isNaN(Number(n))) return '\u2014';
  return new Intl.NumberFormat().format(Number(n));
}

/** Platform label for kit cards. */
export const PLATFORM_LABELS = {
  tiktok: 'TikTok', youtube_shorts: 'YouTube Shorts', instagram_reels: 'Instagram Reels', youtube: 'YouTube', twitter: 'X / Twitter', linkedin: 'LinkedIn',
};
